import { useState, useEffect } from 'react';
import { WifiOff, Wifi } from 'lucide-react';
import { useT } from '../../lib/i18n/I18nContext';

/**
 * OfflineBanner — full-width top bar that makes "offline mode" unmistakable.
 *
 * Amber + a pulsing dot = offline mode is ACTIVE and the app is still working
 * (reads served from cache, edits queued locally). Green flash on reconnect =
 * changes are syncing back. The live count of queued edits is owned by the
 * sibling OfflineSyncIndicator pill so this bar stays a clean state signal.
 *
 * Self-contained on navigator.onLine + window online/offline events (locked by
 * the TICKET-027 sub-D sentinel) — deliberately independent of the sync engine.
 */
export default function OfflineBanner() {
  const { t } = useT();
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showOnline, setShowOnline] = useState(false); // brief "Back online" flash

  useEffect(() => {
    const handleOffline = () => setIsOffline(true);
    const handleOnline = () => {
      setIsOffline(false);
      setShowOnline(true);
      setTimeout(() => setShowOnline(false), 3000); // auto-dismiss after 3s
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  if (!isOffline && !showOnline) return null;

  return (
    <div
      data-testid="offline-banner"
      role="status"
      aria-live="polite"
      style={{ paddingTop: 'calc(8px + env(safe-area-inset-top, 0px))' }}
      className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold transition-all ${
        isOffline
          ? 'bg-amber-500 text-white'
          : 'bg-green-500 text-white'
      }`}
    >
      {isOffline ? (
        <>
          <span
            data-testid="offline-pulse"
            aria-hidden="true"
            className="inline-block w-2 h-2 rounded-full bg-[var(--on-navy)]"
            style={{ animation: 'acn-offline-pulse 1.6s ease-in-out infinite' }}
          />
          <WifiOff className="w-4 h-4" aria-hidden="true" />
          <span>{t('offline.banner')}</span>
        </>
      ) : (
        <>
          <Wifi className="w-4 h-4" aria-hidden="true" />
          <span>{t('offline.backOnline')}</span>
        </>
      )}
      <style>{'@keyframes acn-offline-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.35; transform: scale(0.65); } } @media (prefers-reduced-motion: reduce) { [data-testid="offline-pulse"] { animation: none !important; } }'}</style>
    </div>
  );
}
